import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Tag, Tooltip, message } from 'antd';
import { dateConversion } from '../../utils/javascript';
import { useTranslation } from 'react-i18next';
import { PreviewModalContainer } from './styles';
import {
  PreviewTemplateDetails,
  DesignWrapper
} from './styles';
import ComplexArtboardProvider, { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import ComplexDocumentHistoryProvider, { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import BusinessLogicProvider from '../../context/businessLogicContext';
import { getComplexDocument, getStandaloneFlow, getStandaloneBlock ,getReusableFlow, getMasterDocument} from '../../utils/services/complexDocumentsAPI';
import { SIDEBAR_PREVIEW_MODAL } from '../../containers/SidebarMenu/translationValues';
import { ComplexArtboardWrapper, DocumentContainer, Container as Containers, Wrapper } from '../../containers/ComplexDocumentDesign/styles';
import ComplexArtboard from '../../containers/ComplexDocumentDesign/ComplexArtboard';
import { PREVIEW_MODAL } from './translationValues';
import FontsProvider from '../../context/fontsContext';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import { getEmailStandaloneBlock } from '../../utils/services/complexEmailAPI';

const NAMESPACES = ['SIDEBAR_PREVIEW_MODAL']
const OVERRIDE_ARTBOARD_STATE = {
  [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
  [CONTEXT_KEYS.ENABLE_RULER]: false,
  [CONTEXT_KEYS.SCALING_SETTING]: 0.8,
  [CONTEXT_KEYS.RULER_WIDTH_ADJUSTER]: true,
  [CONTEXT_KEYS.PREVIEW_MODAL] : true,
}

const ComplexPreviewModal = ({ onCancel, type, templateId, templateData = undefined, attachmentHeader = undefined ,reusableFlow = false, isMasterTemplate = false}) => {
  const { t } = useTranslation(NAMESPACES);

  const [preViewTemplate, setPreviewTemplate] = useState({});
  const [masterVariables, setMasterVariables] = useState();

  const gVarSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(gVarSelector, `ComplexPreviewModal-${templateId}`);
  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexElementsTab`);
  

  useEffect(() => {
    if (type === undefined) return;
    (async () => {
      let response;
      const successStatusCode = [707, 2605, 2505, 1905];
      if (templateData) {
        setPreviewTemplate(templateData);
        return;
      }
      if (type === 'document' && isMasterTemplate) {
        const payload = { draftDocumentId: templateId || templateData.id };
        const res = await getMasterDocument(payload);
        response = {};
        if (res?.data?.length) {
          const masterResponse = res.data[0];
          const variables = masterResponse.variables;
          setMasterVariables(variables);
          response.data = masterResponse.documentDTO;
          response.statusCode = res.statusCode;
        }
      } else if (type === "document") {
        response = await getComplexDocument(templateId, COMPLEX_DOCUMENT_VERSION.DRAFT, false);
      } else if (type === 'flow') {     
        if (!reusableFlow) {
          response = await getReusableFlow(templateId, false);
        }
        else {
          response = await getStandaloneFlow(templateId, false);
        }
      } else if (type === 'block') {
        if(isInteractiveDesign) {
          response = await getStandaloneBlock(templateId, COMPLEX_DOCUMENT_VERSION.PUBLISHED, false);
        } else {
          response = await getStandaloneBlock(templateId, COMPLEX_DOCUMENT_VERSION.DRAFT, false);
        }
      } else if (type === 'publishedDocument') {
        response = await getComplexDocument(templateId, COMPLEX_DOCUMENT_VERSION.PUBLISHED, false);
      } else if(type === "email_block") {
        response = await getEmailStandaloneBlock(templateId, COMPLEX_DOCUMENT_VERSION.DRAFT, false);
      }
      if (successStatusCode.includes(response.statusCode)) {
        setPreviewTemplate(response.data);
      } else {
        message.error(t(PREVIEW_MODAL.RESPONSE_ERROR.key, PREVIEW_MODAL.RESPONSE_ERROR.value));
      }
    })();
  }, []);

  return (
    <Modal
      title={null}
      centered
      visible={Object.keys(preViewTemplate).length > 0}
      width="60vw"
      onCancel={onCancel}
      style={{ padding: '0px !important' }}
      footer={null}
      className='previewModalView'
      closable={attachmentHeader === undefined}
    >
      {attachmentHeader !== undefined ? attachmentHeader : (
      <PreviewTemplateDetails>
        <h2>{preViewTemplate?.name || preViewTemplate?.flowName || preViewTemplate.parentBlockName}</h2>
          {preViewTemplate?.updatedOn && <p>
            {preViewTemplate?.updatedOn
              ? t(SIDEBAR_PREVIEW_MODAL.TEMPLATE_EDITED_ON.key, SIDEBAR_PREVIEW_MODAL.TEMPLATE_EDITED_ON.value) + dateConversion(preViewTemplate?.updatedOn, false)
              : t(SIDEBAR_PREVIEW_MODAL.TEMPLATE_EDITED_ON.key, SIDEBAR_PREVIEW_MODAL.TEMPLATE_EDITED_ON.value) + dateConversion(preViewTemplate?.createdOn, false)}
          </p>}
        {preViewTemplate?.tags?.length > 0 && preViewTemplate?.tags && (
          <div className="tags-div">
            {preViewTemplate?.tags.split(',').map(tag => {
              const isLongTag = tag.length > 20;
              const tagElement = (
                <Tag className="edit-tag" key={tag} color="#df4c35">
                  <span>{isLongTag ? `${tag.slice(0, 20)}...` : tag}</span>
                </Tag>
              );
              return isLongTag ? (
                <Tooltip title={tag} key={tag}>
                  {tagElement}
                </Tooltip>
              ) : (
                tagElement
              );
            })}
          </div>
        )}
      </PreviewTemplateDetails>
      )}

      <PreviewModalContainer>
        <DesignWrapper>
          <Containers>
            <Wrapper>
              {Object.keys(preViewTemplate).length > 0 &&
                <ComplexDocumentHistoryProvider masterVariables={masterVariables || globalVariables}>
                  <ComplexArtboardProvider customState={{...OVERRIDE_ARTBOARD_STATE, [CONTEXT_KEYS.CURRENT_DESIGN]: type}} >
                    <BusinessLogicProvider>
                      <FontsProvider>
                        <ComplexArtboardWrapper>
                          <DocumentContainer className={'environment'} >
                            <ComplexArtboard
                              existingDocument={preViewTemplate}
                            />
                          </DocumentContainer>
                        </ComplexArtboardWrapper>
                      </FontsProvider>
                    </BusinessLogicProvider>
                  </ComplexArtboardProvider>
                </ComplexDocumentHistoryProvider>}
            </Wrapper>
          </Containers>
        </DesignWrapper>
      </PreviewModalContainer> 
    </Modal >
  )
}

export default ComplexPreviewModal
